import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    ChevronLeft, ChevronRight, Check, AlertCircle, Award,
    BookOpen, CheckCircle, Activity, ArrowLeft, Timer, Target,
    BarChart3, HelpCircle, Trophy, RefreshCcw, Layers, Info, FileText,
    Video, VideoOff, Zap, Shield, Eye, Flag, Star,
    XCircle, ClipboardList, TrendingUp, SkipForward, LogOut,
    AlertTriangle, CheckSquare, Clock, PlayCircle, ChevronDown,
    Monitor, UserCheck, CheckCircle2, ArrowRight, Camera, Maximize
} from "lucide-react";
import axiosInstance from "../../../../axios";
import Modal from "@/pages/Other/Modal";

/* ─────────────────── helpers ─────────────────── */
const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/* circular SVG timer */
const CircularTimer = ({ total, remaining }) => {
    const r = 16;
    const circ = 2 * Math.PI * r;
    const pct = total > 0 ? remaining / total : 0;
    const offset = circ * (1 - pct);
    const isWarn = pct < 0.25;
    const isCrit = pct < 0.1;
    return (
        <svg width="42" height="42" className="rotate-[-90deg]">
            <circle cx="21" cy="21" r={r} strokeWidth="3" fill="none" className="stroke-white/10" />
            <circle
                cx="21" cy="21" r={r} strokeWidth="3" fill="none"
                strokeDasharray={circ}
                strokeDashoffset={offset}
                strokeLinecap="round"
                className={`transition-all duration-1000 ${isCrit ? "stroke-rose-400" : isWarn ? "stroke-amber-400" : "stroke-white"}`}
            />
        </svg>
    );
};

/* ─────────────────── Skeletons ─────────────────── */
const Sk = ({ className }) => <div className={`bg-slate-200 animate-pulse rounded-xl ${className}`} />;

const SelectionSkeleton = () => (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl border border-slate-200 p-7 space-y-3">
                <div className="flex items-center gap-3 mb-4"><Sk className="w-10 h-10" /><Sk className="h-6 w-24" /></div>
                {[...Array(5)].map((_, i) => <Sk key={i} className="h-14 rounded-2xl" />)}
            </div>
        </div>
        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-5">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-3xl border border-slate-200 p-7 space-y-4">
                    <div className="flex justify-between items-start"><Sk className="w-14 h-14 rounded-2xl" /><Sk className="h-6 w-16 rounded-full" /></div>
                    <Sk className="h-5 w-3/4 rounded-lg" />
                    <Sk className="h-4 w-full rounded-lg" />
                    <Sk className="h-4 w-2/3 rounded-lg" />
                    <div className="flex gap-2 pt-2"><Sk className="h-8 w-24 rounded-xl" /><Sk className="h-8 w-20 rounded-xl" /></div>
                    <Sk className="h-12 w-full rounded-2xl" />
                </div>
            ))}
        </div>
    </div>
);

const QuizSessionSkeleton = () => (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 flex flex-col gap-5">
            <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-200 animate-pulse h-44 px-8 py-7 flex flex-col justify-between">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4"><div className="w-10 h-10 bg-slate-300 rounded-xl" /><div className="space-y-2"><div className="h-5 w-48 bg-slate-300 rounded-lg" /><div className="h-4 w-32 bg-slate-300 rounded-lg" /></div></div>
                        <div className="h-16 w-16 bg-slate-300 rounded-full" />
                    </div>
                    <div className="space-y-2"><div className="flex justify-between"><div className="h-3 w-16 bg-slate-300 rounded" /><div className="h-3 w-24 bg-slate-300 rounded" /></div><div className="h-2 w-full bg-slate-300 rounded-full" /></div>
                </div>
                <div className="p-10 space-y-6">
                    <Sk className="h-5 w-32 rounded-full" />
                    <Sk className="h-7 w-full rounded-lg" />
                    <Sk className="h-6 w-4/5 rounded-lg" />
                    <div className="space-y-3 pt-2">{[...Array(4)].map((_, i) => (<div key={i} className="flex items-center gap-4 p-5 border-2 border-slate-100 rounded-2xl"><Sk className="w-9 h-9 flex-shrink-0" /><Sk className="h-5 flex-1 rounded-lg" /></div>))}</div>
                </div>
                <div className="px-10 py-6 bg-slate-50 border-t border-slate-100 flex justify-between items-center"><Sk className="h-11 w-28 rounded-xl" /><div className="flex gap-3"><Sk className="h-11 w-24 rounded-xl" /><Sk className="h-11 w-36 rounded-xl" /></div></div>
            </div>
        </div>
        <div className="lg:col-span-1 flex flex-col gap-5">
            <div className="bg-slate-900 rounded-3xl p-4"><div className="flex items-center gap-2 mb-3 px-1"><Sk className="w-4 h-4 rounded bg-slate-700" /><Sk className="h-3 w-28 rounded bg-slate-700" /></div><Sk className="aspect-video w-full rounded-xl bg-slate-700" /></div>
            <div className="bg-white rounded-3xl border border-slate-200 p-6 space-y-5"><div className="flex items-center gap-3"><Sk className="w-8 h-8 rounded-xl" /><Sk className="h-4 w-32 rounded-lg" /></div><div className="grid grid-cols-5 gap-2">{[...Array(10)].map((_, i) => <Sk key={i} className="aspect-square rounded-xl" />)}</div></div>
        </div>
    </div>
);

/* ─────────────────── Main Component ─────────────────── */
const QuizExam = () => {
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [quizzes, setQuizzes] = useState([]);
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [quizSession, setQuizSession] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
    const [showResult, setShowResult] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [totalTime, setTotalTime] = useState(0);
    const [isQuizCompleted, setIsQuizCompleted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [protocolStep, setProtocolStep] = useState(0); // 0: none, 1: instructions, 2: camera
    const [showReadyModal, setShowReadyModal] = useState(false);
    const [showTestModeModal, setShowTestModeModal] = useState(false);
    const [testMode, setTestMode] = useState("live");
    const [pendingQuiz, setPendingQuiz] = useState(null);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const [cameraError, setCameraError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverCorrectAnswers, setServerCorrectAnswers] = useState({});
    const [serverResults, setServerResults] = useState(null);
    const [tabSwitchWarnings, setTabSwitchWarnings] = useState(0);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(true);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const questionRef = useRef(null);
    const navigate = useNavigate();

    const selectedCourse = courses.find(c => c.ID === selectedCourseId);
    
    const checkIsCourseStarted = (course) => {
        if (!course) return false;
        if ((course.progress?.finishedSection || 0) > 0) return true;
        try {
            const saved = localStorage.getItem("started_courses");
            if (saved) {
                const startedSet = new Set(JSON.parse(saved).map(String));
                return startedSet.has(String(course.ID || course.id)) || startedSet.has(String(course.CourseName));
            }
        } catch (e) {
            console.error(e);
        }
        return false;
    };
    
    const isCourseStarted = checkIsCourseStarted(selectedCourse);

    /* ── data fetch ── */
    useEffect(() => {
        const fetchCourses = async () => {
            setIsLoading(true);
            try {
                const res = await axiosInstance.get("/course/purchased");
                setCourses(res?.data?.data?.data || []);
            } catch (err) { console.error(err); }
            finally { setIsLoading(false); }
        };
        fetchCourses();
    }, []);

    useEffect(() => {
        if (!selectedCourseId) return;
        const fetchQuizzes = async () => {
            setIsLoading(true);
            try {
                const res = await axiosInstance.get(`/quiz/${selectedCourseId}`);
                setQuizzes(res?.data?.data || []);
            } catch (err) { console.error(err); }
            finally { setIsLoading(false); }
        };
        fetchQuizzes();
    }, [selectedCourseId]);

    /* ── camera cleanup on unmount ── */
    useEffect(() => () => { if (cameraStream) cameraStream.getTracks().forEach(t => t.stop()); }, [cameraStream]);

    /* ── countdown timer ── */
    useEffect(() => {
        if (!quizSession || timeLeft <= 0 || isQuizCompleted) return;
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) { clearInterval(timer); handleQuizEnd(); return 0; }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [quizSession, timeLeft, isQuizCompleted]);

    /* ── smooth scroll to top of question on change ── */
    useEffect(() => {
        if (questionRef.current) questionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [currentQuestionIndex]);

    /* ── distraction free mode ── */
    useEffect(() => {
        if (selectedQuiz && quizSession && !showResult) {
            const topbar = document.getElementById("topbar");
            const footer = document.querySelector("footer");
            if (topbar) topbar.style.display = "none";
            if (footer) footer.style.display = "none";
            return () => {
                if (topbar) topbar.style.display = "block";
                if (footer) footer.style.display = "block";
            };
        }
    }, [selectedQuiz, quizSession, showResult]);

    /* ── Advanced Proctoring & Resilience ── */
    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);



    /* ── camera ── */
    const initCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            setCameraStream(stream);
            setCameraError("");
            return true;
        } catch {
            setCameraError("Unable to access camera. Please check permissions.");
            return false;
        }
    };
    const stopCamera = () => {
        if (cameraStream) { cameraStream.getTracks().forEach(t => t.stop()); setCameraStream(null); }
    };

    /* ── quiz lifecycle ── */
    const startTest = async (quiz) => {
        setSelectedQuiz(quiz);
        setCurrentQuestionIndex(0);
        setUserAnswers({});
        setFlaggedQuestions(new Set());
        setShowResult(false);
        setIsQuizCompleted(false);
        setIsLoading(true);
        try {
            const res = await axiosInstance.post("/quiz/start/test", { quizId: quiz.id, mode: testMode });
            const d = res.data.data;
            setQuizSession({ ...d, id: d.testId || d.id });
            const tl = d.timeLimit || 30 * 60;
            setTimeLeft(tl);
            setTotalTime(tl);
        } catch (err) { console.error(err); }
        finally { setIsLoading(false); }
    };

    const handleStartEvaluationClick = (quiz) => {
        setPendingQuiz(quiz);
        setTestMode("live");
        setAgreedToTerms(false);
        setProtocolStep(1);
    };

    const handleTestModeSelect = (mode) => {
        setTestMode(mode);
        setAgreedToTerms(false);
        setCameraError("");
        setProtocolStep(1);
    };

    const handleProceedToCamera = async () => {
        if (agreedToTerms) {
            if (testMode === "sample") {
                startTest(pendingQuiz);
                setProtocolStep(0);
                setPendingQuiz(null);
            } else {
                setProtocolStep(2);
                await initCamera();
            }
        }
    };

    const handleReadyToStart = () => {
        if (testMode === "sample" || cameraStream) {
            setShowReadyModal(true);
        } else {
            setCameraError("Camera verification is mandatory.");
        }
    };

    const confirmStartTest = () => {
        if (pendingQuiz && (testMode === "sample" || cameraStream)) {
            if (testMode === "live") {
                try { document.documentElement.requestFullscreen(); } catch (e) { console.error(e); }
                setIsFullscreen(true);
            }
            startTest(pendingQuiz);
            setProtocolStep(0);
            setShowReadyModal(false);
            setPendingQuiz(null);
        }
    };

    const handleQuizEnd = useCallback(() => {
        setIsQuizCompleted(true);
        finishQuiz();
    }, []);

    /* ── Advanced Proctoring ── */
    useEffect(() => {
        if (testMode !== "live" || !quizSession || showResult) return;
        
        const handleVisibilityChange = () => {
            if (document.hidden) {
                setTabSwitchWarnings(prev => {
                    const next = prev + 1;
                    if (next >= 3) {
                        handleQuizEnd(); // Force submit
                    } else {
                        setShowWarningModal(true);
                    }
                    return next;
                });
            }
        };

        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        document.addEventListener("fullscreenchange", handleFullscreenChange);
        
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, [testMode, quizSession, showResult, handleQuizEnd]);

    /* ── answer handling ── */
    const handleOptionSelect = (questionId, optionId) => {
        const q = quizSession?.questions?.find(qi => qi.id === questionId);
        const isMultiple = q && (q.type === 3 || q.type === '3');
        
        setUserAnswers(prev => {
            if (isMultiple) {
                const currentSelections = Array.isArray(prev[questionId]) ? prev[questionId] : [];
                const nextSelections = currentSelections.includes(optionId)
                    ? currentSelections.filter(id => id !== optionId)
                    : [...currentSelections, optionId];
                return { ...prev, [questionId]: nextSelections };
            } else {
                return { ...prev, [questionId]: optionId };
            }
        });
    };

    const toggleFlag = (questionId) => {
        setFlaggedQuestions(prev => {
            const next = new Set(prev);
            next.has(questionId) ? next.delete(questionId) : next.add(questionId);
            return next;
        });
    };

    const submitAnswer = async (questionId, selections) => {
        if (!selections) return;
        const answerIds = Array.isArray(selections) ? selections : [selections];
        if (answerIds.length === 0) return;
        try {
            const res = await axiosInstance.post("/quiz/answer/submit", {
                testId: quizSession.id,
                questionId,
                answerIds: answerIds,
            });
            
            // Capture correct answer from server if provided
            if (res.data?.data?.rightAnswer) {
                setServerCorrectAnswers(prev => ({ ...prev, [questionId]: res.data.data.rightAnswer }));
            }

            // Check if final results are returned
            if (res.data?.message === "Test results") {
                setServerResults(res.data.data);
            }

            if (currentQuestionIndex < quizSession.questions.length - 1) {
                setCurrentQuestionIndex(prev => prev + 1);
            } else {
                handleQuizEnd();
            }
        } catch (err) { console.error(err); }
    };

    const finishQuiz = async () => {
        setIsSubmitting(true);
        try {
            setIsQuizCompleted(true);
            setShowResult(true);
            stopCamera();
        } catch (err) { console.error(err); }
        finally { setIsSubmitting(false); }
    };

    const handleSubmitAll = () => {
        const unanswered = (quizSession?.questions?.length || 0) - getAnsweredCount();
        if (unanswered > 0 || flaggedQuestions.size > 0) {
            setShowSubmitConfirm(true);
        } else {
            finishQuiz();
        }
    };

    const handleExitClick = () => setShowExitConfirm(true);

    const resetQuiz = () => {
        setSelectedQuiz(null);
        setQuizSession(null);
        setCurrentQuestionIndex(0);
        setUserAnswers({});
        setFlaggedQuestions(new Set());
        setShowResult(false);
        setIsQuizCompleted(false);
        setShowExitConfirm(false);
        stopCamera();
    };

    /* ── navigation ── */
    const skipQuestion = () => { if (currentQuestionIndex < quizSession.questions.length - 1) setCurrentQuestionIndex(p => p + 1); };
    const prevQuestion = () => { if (currentQuestionIndex > 0) setCurrentQuestionIndex(p => p - 1); };
    const navigateToQuestion = (i) => setCurrentQuestionIndex(i);

    /* ── computed ── */
    const getCurrentQuestion = () => quizSession?.questions?.[currentQuestionIndex] ?? null;
    const getAnsweredCount = () => Object.keys(userAnswers).filter(key => {
        const ans = userAnswers[key];
        if (ans === undefined || ans === null) return false;
        if (Array.isArray(ans)) return ans.length > 0;
        return true;
    }).length;
    const getProgressPct = () => {
        if (!quizSession?.questions?.length) return 0;
        return Math.floor((getAnsweredCount() / quizSession.questions.length) * 100);
    };
    const timerPct = totalTime > 0 ? timeLeft / totalTime : 0;
    const timerState = timerPct < 0.1 ? "critical" : timerPct < 0.25 ? "warning" : "normal";

    /* ─────────── Result Screen ─────────── */
    const renderResult = () => {
        if (!quizSession?.questions) return null;
        const total = quizSession.questions.length;
        const answered = getAnsweredCount();
        
        // Use server results if available, otherwise fallback to frontend calculation
        const correct = serverResults ? serverResults.rightAns : quizSession.questions.filter(q => {
            const selected = userAnswers[String(q.id)];
            if (!selected) return false;
            
            const isMultiple = q.type === 3 || q.type === '3';
            if (isMultiple) {
                const correctIds = q.options?.filter(o => o.isRight || o.isCorrect).map(o => String(o.id)) || [];
                const selectedIds = Array.isArray(selected) ? selected.map(String) : [String(selected)];
                return correctIds.length === selectedIds.length && 
                       correctIds.every(id => selectedIds.includes(id));
            } else {
                const correctId = q.correctOptionId || q.options?.find(o => o.isRight || o.isCorrect)?.id;
                return String(selected) === String(correctId);
            }
        }).length;

        const wrong = serverResults ? serverResults.wrongAns : (answered - correct);
        const skipped = total - answered;
        const score = total > 0 ? ((correct / total) * 100) : 0;
        const scoreFixed = score.toFixed(1);
        const timeTaken = totalTime - timeLeft;

        const grade =
            score >= 90 ? { label: "A+", color: "emerald", title: "Outstanding!", msg: "Exceptional mastery of the material." }
            : score >= 80 ? { label: "A",  color: "emerald", title: "Excellent!",   msg: "Strong command demonstrated." }
            : score >= 70 ? { label: "B",  color: "blue",    title: "Good Job!",    msg: "Solid understanding. Keep it up." }
            : score >= 60 ? { label: "C",  color: "amber",   title: "Satisfactory", msg: "Room to grow — review and retry." }
            : { label: "F",  color: "rose",    title: "Keep Going",   msg: "Review the material and try again." };

        /* SVG arc for result circle */
        const R = 52, C = 2 * Math.PI * R;
        const arcOffset = C * (1 - score / 100);

        return (
            <div className="max-w-3xl mx-auto space-y-4">
                {/* Hero card */}
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="bg-gradient-to-br from-[#1e3a6e] via-[#264b82] to-indigo-700 px-8 py-7 text-white relative overflow-hidden">
                        <div className="absolute inset-0 opacity-5 pointer-events-none">
                            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full border-2 border-white" />
                            <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full border-2 border-white" />
                        </div>
                        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-8">
                            {/* Circular score */}
                            <div className="relative flex-shrink-0">
                                <svg width="128" height="128" className="rotate-[-90deg]">
                                    <circle cx="64" cy="64" r={R} strokeWidth="8" fill="none" className="stroke-white/10" />
                                    <circle cx="64" cy="64" r={R} strokeWidth="8" fill="none" strokeLinecap="round"
                                        strokeDasharray={C} strokeDashoffset={arcOffset}
                                        className={`transition-all duration-1000 ${
                                            score >= 70 ? "stroke-emerald-400" : score >= 50 ? "stroke-amber-400" : "stroke-rose-400"
                                        }`}
                                    />
                                </svg>
                                {/* <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-3xl font-black text-white">{scoreFixed}<span className="text-lg text-white/60">%</span></span>
                                    <span className={`text-xs font-black tracking-widest uppercase mt-0.5 text-${grade.color}-300`}>{grade.label}</span>
                                </div> */}
                            </div>
                            {/* Title */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    {score >= 70 ? <Trophy className="w-6 h-6 text-yellow-300" /> : <Award className="w-6 h-6 text-blue-200" />}
                                    <h3 className="text-3xl font-black">{grade.title}</h3>
                                </div>
                                <p className="text-blue-100 text-base font-semibold leading-relaxed mb-4">{grade.msg}</p>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        { icon: <CheckCircle size={14} />, label: `${correct} Correct`,    cls: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30" },
                                        { icon: <XCircle     size={14} />, label: `${wrong} Wrong`,       cls: "bg-rose-500/20 text-rose-200 border-rose-400/30" },
                                        { icon: <SkipForward size={14} />, label: `${skipped} Skipped`,   cls: "bg-white/10 text-white/70 border-white/20" },
                                        { icon: <Clock       size={14} />, label: formatTime(timeTaken),  cls: "bg-white/10 text-white/70 border-white/20" },
                                    ].map(b => (
                                        <span key={b.label} className={`flex items-center gap-1.5 text-sm font-black px-3.5 py-2 rounded-xl border ${b.cls}`}>
                                            {b.icon}{b.label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Score bar breakdown */}
                    <div className="px-7 py-4 border-b border-slate-100">
                        <div className="flex justify-between text-sm font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                            <span>Score Breakdown</span><span>{scoreFixed}% / 100%</span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden flex">
                            <div className="h-full bg-emerald-500 transition-all duration-1000" style={{ width: `${(correct / total) * 100}%` }} />
                            <div className="h-full bg-rose-400 transition-all duration-1000" style={{ width: `${(wrong / total) * 100}%` }} />
                            <div className="h-full bg-slate-200 transition-all duration-1000" style={{ width: `${(skipped / total) * 100}%` }} />
                        </div>
                        <div className="flex gap-4 mt-2">
                            {[["bg-emerald-500","Correct"],["bg-rose-400","Wrong"],["bg-slate-200","Skipped"]].map(([c,l]) => (
                                <div key={l} className="flex items-center gap-1.5 text-sm font-black text-slate-500">
                                    <div className={`w-2.5 h-2.5 rounded-full ${c}`} />{l}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Stats grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-slate-100">
                        {[
                            { label: "Total",    value: total,                 sub: "questions",    icon: <ClipboardList size={18} className="text-slate-400" /> },
                            { label: "Correct",  value: correct,               sub: "answers",      icon: <CheckCircle   size={18} className="text-emerald-500" /> },
                            { label: "Accuracy", value: `${scoreFixed}%`,      sub: "score",        icon: <TrendingUp    size={18} className="text-blue-500" /> },
                            { label: "Time",     value: formatTime(timeTaken), sub: "taken",        icon: <Clock         size={18} className="text-amber-500" /> },
                        ].map(s => (
                            <div key={s.label} className="flex items-center gap-3 p-4">
                                <div className="bg-slate-50 p-2 rounded-xl">{s.icon}</div>
                                <div>
                                    <p className="text-sm font-black uppercase tracking-wider text-slate-400">{s.label}</p>
                                    <p className="text-3xl font-black text-slate-800 leading-none mt-1">{s.value}</p>
                                    <p className="text-sm text-slate-400 font-extrabold mt-0.5">{s.sub}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="p-5 flex flex-col sm:flex-row gap-3 border-t border-slate-100">
                        <button onClick={resetQuiz}
                            className="flex-1 flex items-center justify-center gap-2 bg-[#264b82] text-white px-6 py-4.5 rounded-2xl font-black text-base uppercase tracking-wider hover:bg-[#1e3a6e] transition-all shadow-lg shadow-blue-900/15 active:scale-95">
                            <RefreshCcw size={18} strokeWidth={2.5} /> Try Another Quiz
                        </button>
                        <button onClick={() => navigate(-1)}
                            className="flex-1 flex items-center justify-center gap-2 bg-slate-50 text-slate-700 border border-slate-200 px-6 py-4.5 rounded-2xl font-black text-base uppercase tracking-wider hover:bg-slate-100 transition-all active:scale-95">
                            <ArrowLeft size={18} strokeWidth={2.5} /> Back to Dashboard
                        </button>
                    </div>
                </div>

                {/* Question review accordion */}
                <QuestionReview 
                    questions={quizSession.questions} 
                    userAnswers={userAnswers} 
                    serverCorrectAnswers={serverCorrectAnswers} 
                />
            </div>
        );
    };

    /* ─────────── Quiz Session UI ─────────── */
    const currentQ = getCurrentQuestion();

    /* ─────────── Main Render ─────────── */
    return (
        <section style={{ fontFamily: "'Inter', sans-serif" }} className={`min-h-screen bg-[#eef2f7] pb-10 px-4 md:px-6 relative transition-all duration-500 ${selectedQuiz && quizSession && !showResult ? "pt-5" : "pt-5"}`}>
            <div className="max-w-7xl mx-auto">

                {/* Page header */}
                {!isLoading && (
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-7 gap-3">
                        <div className="flex items-center gap-4">
                            {!selectedQuiz && (
                                <button onClick={() => navigate(-1)} 
                                    className="flex items-center justify-center w-10 h-10 bg-white text-slate-400 hover:text-slate-600 rounded-xl border border-slate-200 transition-all shadow-sm active:scale-95"
                                    title="Exit to Dashboard">
                                    <ChevronLeft size={20} strokeWidth={3} />
                                </button>
                            )}
                            <div>
                                <h2 className="text-3xl font-black text-slate-800 tracking-tight">
                                    {selectedQuiz ? selectedQuiz.name : "Skill Assessment"}
                                </h2>
                                <p className="text-slate-500 text-sm font-medium mt-0.5">
                                    {selectedQuiz && !showResult
                                        ? `${getAnsweredCount()} of ${quizSession?.questions?.length || 0} questions answered`
                                        : showResult ? "Assessment complete — review your performance below"
                                        : courses.length === 0 ? "You must purchase a course to take assessments"
                                        : selectedCourseId ? (
                                            isCourseStarted ? "Select an assessment to begin" : "Start this course to unlock assessments"
                                        ) : "Choose a course then select an assessment to begin"}
                                </p>
                            </div>
                        </div>
                        {selectedQuiz && !showResult && (
                            <button onClick={handleExitClick}
                                className="flex items-center gap-2 bg-white text-rose-500 hover:bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm active:scale-95">
                                <LogOut size={15} /> Exit Session
                            </button>
                        )}
                    </div>
                )}

                {isLoading ? (
                    selectedQuiz ? <QuizSessionSkeleton /> : <SelectionSkeleton />
                ) : selectedQuiz && quizSession && !showResult ? (
                    /* ─── QUIZ SESSION ─── */
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

                        {/* Main column */}
                        <div className={`lg:col-span-3 flex flex-col gap-3.5 ${testMode === "live" ? "select-none" : ""}`} ref={questionRef}
                            onContextMenu={(e) => { if (testMode === "live") e.preventDefault(); }}
                            onCopy={(e) => { if (testMode === "live") e.preventDefault(); }}
                            onPaste={(e) => { if (testMode === "live") e.preventDefault(); }}
                            onCut={(e) => { if (testMode === "live") e.preventDefault(); }}
                        >

                            {/* Quiz header bar */}
                            {/* Quiz header - Sticky in session */}
                            <div className={`bg-gradient-to-r from-[#1e3a6e] to-[#264b82] rounded-2xl px-4 py-2.5 text-white relative overflow-hidden shadow-xl shadow-blue-900/20 transition-all duration-300 ${
                                selectedQuiz && quizSession && !showResult ? "sticky top-2 z-[100] mb-3.5" : "mb-3.5"
                            }`}>
                                <div className="absolute inset-0 opacity-5 pointer-events-none select-none">
                                    <BarChart3 className="absolute right-6 top-2 w-32 h-32" />
                                </div>
                                <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                    {/* Left: quiz info */}
                                    <div className="flex items-center gap-3">
                                        <div className="bg-white/10 border border-white/20 p-1.5 rounded-xl">
                                            <Layers className="w-3.5 h-3.5 text-white" />
                                        </div>
                                        <div>
                                            <p className="text-white/60 text-[9px] font-extrabold uppercase tracking-widest mb-0.5">{selectedQuiz.name}</p>
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <span className="flex items-center gap-1 bg-white/10 text-white/95 text-[10px] font-extrabold px-1.5 py-0.5 rounded-md border border-white/10">
                                                    <HelpCircle size={10} /> Q {currentQuestionIndex + 1}/{quizSession.questions.length}
                                                </span>
                                                <span className="flex items-center gap-1 bg-emerald-500/20 text-emerald-200 text-[10px] font-extrabold px-1.5 py-0.5 rounded-md border border-emerald-400/20">
                                                    <CheckCircle size={10} /> {getAnsweredCount()} Answered
                                                </span>
                                                {flaggedQuestions.size > 0 && (
                                                    <span className="flex items-center gap-1 bg-amber-500/20 text-amber-200 text-[10px] font-extrabold px-1.5 py-0.5 rounded-md border border-amber-400/20">
                                                        <Flag size={10} /> {flaggedQuestions.size} Flagged
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: timer readout */}
                                    <div className={`flex items-center gap-2.5 px-3 py-1 rounded-xl border transition-all duration-300 ${
                                        timerState === "critical" ? "bg-rose-500/20 border-rose-500/50 shadow-[0_0_20px_rgba(244,63,94,0.2)] animate-pulse"
                                        : timerState === "warning" ? "bg-amber-500/20 border-amber-500/50" : "bg-white/10 border-white/20"
                                    }`}>
                                        <div className="relative flex-shrink-0">
                                            <CircularTimer total={totalTime} remaining={timeLeft} />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className={`font-mono text-xl font-black leading-none tracking-tight ${
                                                timerState === "critical" ? "text-rose-300"
                                                : timerState === "warning" ? "text-amber-300" : "text-white"
                                            }`}>{formatTime(timeLeft)}</span>
                                            <span className="text-white/40 text-[8px] font-black uppercase tracking-widest mt-0.5">Time Remaining</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Progress bar */}
                                <div className="mt-2 relative z-10">
                                    <div className="flex justify-between text-[8px] font-bold text-white/50 uppercase tracking-widest mb-0.5">
                                        <span>Progress</span><span>{getProgressPct()}%</span>
                                    </div>
                                    <div className="h-0.5 bg-white/10 rounded-full overflow-hidden">
                                        <div className="h-full bg-white rounded-full transition-all duration-700 shadow-[0_0_8px_rgba(255,255,255,0.5)]"
                                            style={{ width: `${getProgressPct()}%` }} />
                                    </div>
                                </div>
                            </div>

                            {/* Question card */}
                            {currentQ && (
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                    {/* Question header strip */}
                                    <div className="flex items-center justify-between px-4 pt-3.5 pb-0.5">
                                        <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center justify-center w-7 h-7 bg-[#264b82] text-white rounded-lg text-[11px] font-black">
                                                {currentQuestionIndex + 1}
                                            </span>
                                            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                                                of {quizSession.questions.length} Questions
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => toggleFlag(currentQ.id)}
                                            title={flaggedQuestions.has(currentQ.id) ? "Remove flag" : "Flag for review"}
                                            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-extrabold transition-all ${
                                                flaggedQuestions.has(currentQ.id)
                                                    ? "bg-amber-50 text-amber-600 border border-amber-200"
                                                    : "bg-slate-50 text-slate-400 border border-slate-100 hover:bg-amber-50 hover:text-amber-500 hover:border-amber-200"
                                            }`}>
                                            <Flag size={11} />
                                            {flaggedQuestions.has(currentQ.id) ? "Flagged" : "Flag"}
                                        </button>
                                    </div>

                                    {/* Question text */}
                                    <div className="px-4 pt-2.5 pb-3.5">
                                        <h4 className="text-lg md:text-xl font-black text-slate-800 leading-snug tracking-tight">{currentQ.question}</h4>
                                    </div>

                                    {/* Options */}
                                    <div className="px-4 pb-4 space-y-2">
                                        {currentQ.options?.map((opt, idx) => {
                                            const isMultiple = currentQ.type === 3 || currentQ.type === '3';
                                            const selected = isMultiple
                                                ? (Array.isArray(userAnswers[currentQ.id]) && userAnswers[currentQ.id].includes(opt.id))
                                                : userAnswers[currentQ.id] === opt.id;
                                            return (
                                                <button
                                                    key={opt.id}
                                                    onClick={() => handleOptionSelect(currentQ.id, opt.id)}
                                                    className={`group w-full flex items-center gap-2.5 p-2.5 rounded-xl text-left transition-all duration-200 border-2 ${
                                                        selected
                                                            ? "border-[#264b82] bg-[#264b82]/5 shadow-md shadow-blue-900/8"
                                                            : "border-slate-100 hover:border-blue-200 hover:bg-blue-50/40 bg-white"
                                                    }`}
                                                >
                                                    <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs transition-all ${
                                                        selected ? "bg-[#264b82] text-white" : "bg-slate-100 text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600"
                                                    }`}>
                                                        {selected ? <Check size={12} strokeWidth={3} /> : String.fromCharCode(65 + idx)}
                                                    </div>
                                                    <span className={`text-sm font-bold ${selected ? "text-slate-800" : "text-slate-600 group-hover:text-slate-700"}`}>{opt.answer}</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Navigation footer */}
                                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                                        <button
                                            onClick={prevQuestion}
                                            disabled={currentQuestionIndex === 0}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-[11px] text-slate-500 border border-slate-200 bg-white hover:bg-slate-50 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                                            <ChevronLeft size={13} /> Prev
                                        </button>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={skipQuestion}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-[11px] text-slate-500 hover:bg-slate-100 transition-all active:scale-95">
                                                Skip <SkipForward size={11} />
                                            </button>
                                            {(() => {
                                                const ans = userAnswers[currentQ.id];
                                                const isMultiple = currentQ.type === 3 || currentQ.type === '3';
                                                const hasAnswered = ans !== undefined && ans !== null && (!isMultiple || (Array.isArray(ans) && ans.length > 0));
                                                return (
                                                    <button
                                                        onClick={() => submitAnswer(currentQ.id, ans)}
                                                        disabled={!hasAnswered}
                                                        className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-bold text-[11px] text-white transition-all shadow-lg ${
                                                            !hasAnswered
                                                                ? "bg-slate-300 cursor-not-allowed shadow-none"
                                                                : "bg-[#264b82] hover:bg-[#1e3a6e] shadow-blue-900/20 active:scale-95"
                                                        }`}>
                                                        {currentQuestionIndex === quizSession.questions.length - 1 ? "Finish" : "Next"}
                                                        <ChevronRight size={13} />
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Submit all bar */}
                            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
                                <div>
                                    <p className="font-bold text-slate-700 text-[11px]">Done reviewing?</p>
                                    <p className="text-slate-400 text-[9px] mt-0.5">
                                        {getAnsweredCount()}/{quizSession.questions.length} answered
                                        {flaggedQuestions.size > 0 && ` · ${flaggedQuestions.size} flagged`}
                                    </p>
                                </div>
                                <button onClick={handleSubmitAll} disabled={isSubmitting}
                                    className="flex items-center gap-1.5 bg-[#264b82] text-white hover:bg-[#1e3a6e] px-3.5 py-1.5 rounded-lg font-bold text-[11px] transition-all active:scale-95 shadow-lg shadow-blue-900/15 disabled:opacity-60">
                                    <CheckSquare size={12} />
                                    {isSubmitting ? "Submitting…" : "Submit Quiz"}
                                </button>
                            </div>
                        </div>

                        {/* ─── Sidebar ─── */}
                        <div className="lg:col-span-1">
                            <div className="flex flex-col gap-2.5 sticky top-3">

                                {/* Camera */}
                                {cameraStream ? (
                                    <div className="bg-slate-900 rounded-2xl p-2.5 border border-slate-800 shadow-xl">
                                        <div className="flex items-center justify-between mb-2 px-1">
                                            <div className="flex items-center gap-1.5">
                                                <Shield size={11} className="text-emerald-400" />
                                                <span className="text-white/60 text-[8px] font-black uppercase tracking-widest">
                                                    {testMode === "live" ? "Live Monitor" : "Practice Monitor"}
                                                </span>
                                            </div>
                                            <span className="flex items-center gap-1 text-[7.5px] font-black uppercase tracking-widest bg-rose-500 text-white px-1 py-0.5 rounded-md animate-pulse">
                                                <span className="w-1 h-1 rounded-full bg-white inline-block" /> REC
                                            </span>
                                        </div>
                                        <CameraFeed stream={cameraStream} />
                                    </div>
                                ) : (
                                    <div className="bg-blue-50 rounded-2xl p-3 border border-blue-100 flex flex-col items-center justify-center text-center shadow-sm">
                                        <div className="bg-white p-2 rounded-xl shadow-sm mb-1.5">
                                            <Shield size={16} className="text-blue-500" />
                                        </div>
                                        <h4 className="font-bold text-blue-900 text-[11px] mb-0.5">Practice Mode</h4>
                                        <p className="text-blue-600/70 text-[8px] font-bold uppercase tracking-widest">Camera Not Active</p>
                                    </div>
                                )}

                                {/* Quick stats */}
                                <div className="bg-white rounded-2xl border border-slate-200 p-3 shadow-sm">
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {[
                                            { label: "Done",    value: getAnsweredCount(),                                           color: "text-emerald-600", bg: "bg-emerald-50" },
                                            { label: "Left",    value: quizSession.questions.length - getAnsweredCount(),            color: "text-slate-500",   bg: "bg-slate-50"   },
                                            { label: "Flagged", value: flaggedQuestions.size,                                        color: "text-amber-600",   bg: "bg-amber-50"   },
                                        ].map(s => (
                                            <div key={s.label} className={`${s.bg} rounded-xl p-1.5 text-center`}>
                                                <p className={`text-lg font-black ${s.color}`}>{s.value}</p>
                                                <p className="text-slate-400 text-[9px] font-black uppercase tracking-widest mt-0.5">{s.label}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Question map */}
                                <div className="bg-white rounded-2xl border border-slate-200 p-3 shadow-sm">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="bg-[#264b82]/10 p-1.5 rounded-lg">
                                            <Target size={13} className="text-[#264b82]" />
                                        </div>
                                        <h4 className="font-black text-slate-800 text-xs uppercase tracking-widest">Question Map</h4>
                                    </div>

                                    <div className="grid grid-cols-5 gap-0.5">
                                        {quizSession.questions?.map((q, i) => {
                                            const isCurrent  = currentQuestionIndex === i;
                                            const isAnswered = !!userAnswers[q.id];
                                            const isFlagged  = flaggedQuestions.has(q.id);
                                            return (
                                                <button key={q.id} onClick={() => navigateToQuestion(i)}
                                                    title={`Q${i + 1}${isFlagged ? " · Flagged" : ""}`}
                                                    className={`relative aspect-square flex items-center justify-center rounded-md text-[10px] font-black transition-all border ${
                                                        isCurrent  ? "border-[#264b82] bg-[#264b82] text-white scale-105 shadow-md shadow-blue-900/20 z-10"
                                                        : isAnswered ? "border-emerald-400 bg-emerald-500 text-white"
                                                        : "border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-300 hover:bg-slate-100"
                                                    }`}>
                                                    {isAnswered && !isCurrent ? <Check size={10} strokeWidth={3} /> : i + 1}
                                                    {isFlagged && !isCurrent && (
                                                        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-400 rounded-full border border-white" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="mt-2 pt-1.5 border-t border-slate-100 grid grid-cols-2 gap-y-1 gap-x-2">
                                        {[
                                            { dot: "bg-[#264b82]",        label: "Current"  },
                                            { dot: "bg-emerald-500",      label: "Answered" },
                                            { dot: "bg-slate-200 border", label: "Pending"  },
                                            { dot: "bg-amber-400",        label: "Flagged"  },
                                        ].map(({ dot, label }) => (
                                            <div key={label} className="flex items-center gap-1 text-[9px] font-bold text-slate-500">
                                                <div className={`w-1.5 h-1.5 rounded-full border border-transparent ${dot}`} />{label}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                ) : pendingQuiz ? (
                    <AssessmentProtocol
                        step={protocolStep}
                        setStep={setProtocolStep}
                        quiz={pendingQuiz}
                        agreed={agreedToTerms}
                        setAgreed={setAgreedToTerms}
                        onProceed={handleProceedToCamera}
                        onReady={handleReadyToStart}
                        cameraStream={cameraStream}
                        initCamera={initCamera}
                        cameraError={cameraError}
                        onCancel={() => { setProtocolStep(0); setPendingQuiz(null); stopCamera(); }}
                        testMode={testMode}
                        onSelectMode={handleTestModeSelect}
                    />
                ) : showResult ? renderResult() : courses.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white rounded-[2rem] border border-slate-200 p-8 md:p-16 text-center max-w-2xl mx-auto shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-blue-50 w-24 h-24 rounded-3xl flex items-center justify-center mb-6 shadow-sm border border-blue-100">
                            <BookOpen size={48} className="text-[#264b82]" />
                        </div>
                        <h3 className="text-2xl font-black text-slate-800 tracking-tight mb-3">
                            you are not purchased any courses 
                        </h3>
                        <p className="text-slate-500 text-base font-semibold max-w-md mb-8 leading-relaxed">
                            It looks like you haven't enrolled in any courses yet. Expand your skills and unlock assessments by exploring our course catalog.
                        </p>
                        <button
                            onClick={() => navigate("/courses")}
                            className="group flex items-center gap-2 bg-[#264b82] text-white hover:bg-[#1e3a6e] px-8 py-4 rounded-2xl font-black text-base uppercase tracking-wider transition-all shadow-lg shadow-blue-900/15 active:scale-95 border border-[#264b82]"
                        >
                            explore course <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </div>
                ) : (
                    /* ─── COURSE & QUIZ SELECTION ─── */
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
                        {/* Course sidebar */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 sticky top-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="bg-[#264b82] p-2.5 rounded-xl shadow-lg shadow-blue-900/20">
                                        <BookOpen size={18} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-black text-slate-800 text-base uppercase tracking-wider">My Courses</h3>
                                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">{courses.length} enrolled</p>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    {courses?.map(course => {
                                        const isCourseStarted = checkIsCourseStarted(course);
                                        return (
                                            <button key={course.ID} onClick={() => setSelectedCourseId(course.ID)}
                                                className={`w-full text-left px-5 py-4 rounded-2xl text-base font-extrabold transition-all flex items-center justify-between group border-2 ${
                                                    selectedCourseId === course.ID
                                                        ? "bg-[#264b82] border-[#264b82] text-white shadow-lg shadow-blue-900/15"
                                                        : "bg-white border-slate-100 text-slate-600 hover:border-blue-200 hover:bg-blue-50/40"
                                                }`}>
                                                <span className="pr-2 leading-snug line-clamp-2 flex items-center gap-2">
                                                    {!isCourseStarted && <span className="w-2.5 h-2.5 rounded-full bg-rose-450 bg-rose-400 animate-pulse" title="Not Started" />}
                                                    {course.CourseName}
                                                </span>
                                                <ChevronRight size={14} className={`flex-shrink-0 ${selectedCourseId === course.ID ? "text-white/60" : "text-slate-300"}`} />
                                            </button>
                                        );
                                    })}
                                    {courses.length === 0 && (
                                        <div className="text-center py-10">
                                            <div className="bg-slate-50 rounded-2xl p-4 inline-block mb-2">
                                                <BookOpen className="text-slate-300 w-7 h-7" />
                                            </div>
                                            <p className="text-slate-400 text-sm font-medium">No courses enrolled</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Quiz grid */}
                        <div className="lg:col-span-3">
                            {selectedCourseId ? (
                                isCourseStarted ? (
                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="bg-amber-100 p-2 rounded-xl">
                                                <Zap size={18} className="text-amber-600" />
                                            </div>
                                            <h3 className="text-2xl font-black text-slate-800 tracking-tight">Available Assessments</h3>
                                            <span className="bg-slate-100 text-slate-600 text-sm font-extrabold px-3 py-1.5 rounded-full">{quizzes.length}</span>
                                        </div>

                                        {quizzes.length > 0 ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                {quizzes.map(quiz => (
                                                    <QuizCard key={quiz.id} quiz={quiz} onStart={handleStartEvaluationClick} />
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="bg-white rounded-3xl border border-slate-200 p-14 text-center">
                                                <div className="bg-slate-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                                    <AlertCircle size={30} className="text-slate-300" />
                                                </div>
                                                <h4 className="text-lg font-bold text-slate-700 mb-1">No Assessments Found</h4>
                                                <p className="text-slate-400 text-sm font-medium max-w-xs mx-auto">No quizzes are assigned to this course yet.</p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    /* Locked course view ("look page") */
                                    <div className="flex flex-col items-center justify-center min-h-[50vh] bg-white rounded-[2rem] border border-slate-200 p-8 md:p-12 text-center max-w-xl mx-auto shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                                        <div className="bg-amber-50 w-20 h-20 rounded-3xl flex items-center justify-center mb-6 shadow-sm border border-amber-100">
                                            <PlayCircle size={40} className="text-amber-500" />
                                        </div>
                                        <h3 className="text-xl font-black text-slate-800 tracking-tight mb-3 max-w-lg leading-snug">
                                            This course has not been started yet
                                        </h3>
                                        <p className="text-slate-500 text-sm font-semibold max-w-sm mb-6 leading-relaxed">
                                            To unlock assessments and exams for "{selectedCourse?.CourseName}", you must first start learning it.
                                        </p>
                                        <button
                                            onClick={() => navigate("/dashboard/course/list")}
                                            className="group flex items-center gap-2 bg-[#264b82] text-white hover:bg-[#1e3a6e] px-6 py-3.5 rounded-2xl font-black text-sm uppercase tracking-wider transition-all shadow-lg shadow-blue-900/15 active:scale-95 border border-[#264b82]"
                                        >
                                            Start Learning <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                                        </button>
                                    </div>
                                )
                            ) : (
                                <div className="bg-white rounded-3xl border border-slate-200 min-h-[420px] flex flex-col items-center justify-center text-center p-14">
                                    <div className="bg-[#264b82]/8 w-20 h-20 rounded-3xl flex items-center justify-center mb-5">
                                        <Activity size={40} className="text-[#264b82]" />
                                    </div>
                                    <h4 className="text-xl font-bold text-slate-800 mb-2">Ready for your exam?</h4>
                                    <p className="text-slate-400 text-sm font-medium max-w-xs leading-relaxed">
                                        Select a course from the sidebar to see available assessments.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Offline Overlay */}
            {isOffline && (
                <div className="fixed top-0 left-0 w-full bg-rose-600 text-white p-3 z-[4000] flex items-center justify-center gap-3 font-bold text-sm shadow-lg animate-in slide-in-from-top duration-300">
                    <AlertTriangle size={18} className="animate-pulse" />
                    Connection Lost: Please check your internet connection. Attempting to reconnect...
                </div>
            )}

            {/* Fullscreen Overlay */}
            {!isFullscreen && testMode === "live" && quizSession && !showResult && (
                <div className="fixed inset-0 z-[3500] bg-slate-900/90 backdrop-blur-md flex flex-col items-center justify-center text-center p-6">
                    <div className="bg-white/10 p-4 rounded-3xl mb-5 shadow-2xl border border-white/20">
                        <Monitor size={48} className="text-white" />
                    </div>
                    <h2 className="text-3xl font-black text-white mb-3">Fullscreen Required</h2>
                    <p className="text-slate-300 font-medium mb-8 max-w-md">
                        This live assessment must be taken in fullscreen mode to prevent distractions and maintain integrity.
                    </p>
                    <button onClick={() => { try { document.documentElement.requestFullscreen(); } catch (e) { console.error(e); } }}
                        className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-2xl font-black text-sm transition-all shadow-lg shadow-blue-600/30 active:scale-95">
                        Return to Fullscreen
                    </button>
                </div>
            )}

            {/* Modals */}
            <WarningModal
                isOpen={showWarningModal}
                onClose={() => setShowWarningModal(false)}
                warnings={tabSwitchWarnings}
            />
            <ReadyConfirmModal
                isOpen={showReadyModal}
                onClose={() => setShowReadyModal(false)}
                onConfirm={confirmStartTest}
                quiz={pendingQuiz}
            />
            <SubmitConfirmModal
                isOpen={showSubmitConfirm}
                onClose={() => setShowSubmitConfirm(false)}
                onConfirm={() => { setShowSubmitConfirm(false); finishQuiz(); }}
                total={quizSession?.questions?.length || 0}
                answered={getAnsweredCount()}
                flagged={flaggedQuestions.size}
            />
            <ExitConfirmModal
                isOpen={showExitConfirm}
                onClose={() => setShowExitConfirm(false)}
                onConfirm={resetQuiz}
            />
        </section>
    );
};

/* ─────────────────── QuizCard ─────────────────── */
const QuizCard = ({ quiz, onStart }) => (
    <div className="bg-white rounded-3xl border border-slate-200 p-6 flex flex-col hover:shadow-lg hover:shadow-slate-200/60 hover:border-slate-300 transition-all group">
        <div className="flex items-start justify-between mb-3">
            <div className="bg-slate-50 group-hover:bg-[#264b82]/8 p-3 rounded-2xl transition-colors">
                <Target className="text-[#264b82] w-6 h-6" />
            </div>
            <div className="flex items-center gap-2">
                <span className="bg-emerald-50 text-emerald-700 text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full border border-emerald-100">
                    Ready
                </span>
            </div>
        </div>

        <h4 className="font-black text-lg text-slate-800 mb-2 group-hover:text-[#264b82] transition-colors leading-snug">
            {quiz.name}
        </h4>
        {quiz.guidelines && (
            <p className="text-slate-500 text-sm mb-4 line-clamp-2 leading-relaxed font-medium">{quiz.guidelines}</p>
        )}

        <div className="flex flex-wrap gap-2 mt-auto mb-4">
            <span className="flex items-center gap-1.5 bg-blue-50 border border-blue-100 text-blue-600 text-xs font-extrabold px-3.5 py-2 rounded-xl">
                <HelpCircle size={11} /> {quiz.questions?.length || "?"} Questions
            </span>
            <span className="flex items-center gap-1.5 bg-amber-50 border border-amber-100 text-amber-600 text-xs font-extrabold px-3.5 py-2 rounded-xl">
                <Timer size={11} /> {quiz.timeLimit ? `${Math.floor(quiz.timeLimit / 60)} Min` : "Timed"}
            </span>
            {quiz.passingScore && (
                <span className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 text-emerald-600 text-xs font-extrabold px-3.5 py-2 rounded-xl">
                    <Star size={11} /> Pass: {quiz.passingScore}%
                </span>
            )}
        </div>

        <button onClick={() => onStart(quiz)}
            className="w-full bg-[#264b82]/8 text-[#264b82] group-hover:bg-[#264b82] group-hover:text-white py-4 rounded-2xl font-black text-base uppercase tracking-wider transition-all flex items-center justify-center gap-2 active:scale-95 border border-[#264b82]/20 group-hover:border-transparent">
            <PlayCircle size={16} /> Start Evaluation
        </button>
    </div>
);

/* ─────────────────── QuestionReview ─────────────────── */
const QuestionReview = ({ questions, userAnswers, serverCorrectAnswers = {} }) => {
    const [open, setOpen] = useState(false);
    if (!questions?.length) return null;
    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <button onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-8 py-6 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                    <div className="bg-[#264b82]/10 p-2.5 rounded-xl">
                        <ClipboardList size={18} className="text-[#264b82]" />
                    </div>
                    <span className="font-black text-slate-700 text-lg">Answer Review</span>
                    <span className="bg-slate-100 text-slate-500 text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full">{questions.length} questions</span>
                </div>
                <ChevronDown size={20} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="border-t border-slate-100 divide-y divide-slate-50">
                    {questions.map((q, i) => {
                        const selected = userAnswers[String(q.id)];
                        
                        const isMultiple = q.type === 3 || q.type === '3';
                        const answered = selected !== undefined && (!isMultiple || (Array.isArray(selected) && selected.length > 0));
                        
                        const correctId = q.correctOptionId || q.options?.find(o => o.isRight || o.isCorrect)?.id;
                        const serverCorrect = serverCorrectAnswers[String(q.id)];
                        
                        const serverCorrectIds = Array.isArray(serverCorrect)
                            ? serverCorrect.map(item => String(item?.id ?? item))
                            : serverCorrect
                                ? [String(serverCorrect?.id ?? serverCorrect)]
                                : (correctId ? [String(correctId)] : []);
                                
                        const selectedIds = Array.isArray(selected) 
                            ? selected.map(String) 
                            : selected 
                                ? [String(selected)] 
                                : [];
                                
                        const isRight = answered && (
                            serverCorrectIds.length === selectedIds.length &&
                            serverCorrectIds.every(id => selectedIds.includes(id))
                        );

                        return (
                            <div key={q.id} className="px-8 py-6">
                                <div className="flex items-start gap-4 mb-4">
                                    <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-base font-black mt-0.5 ${
                                        !answered ? "bg-slate-100 text-slate-400"
                                        : isRight  ? "bg-emerald-500 text-white"
                                        : "bg-rose-500 text-white"
                                    }`}>
                                        {!answered ? i + 1 : isRight ? <Check size={14} strokeWidth={3} /> : <XCircle size={14} />}
                                    </div>
                                    <p className="text-lg font-black text-slate-800 leading-snug flex-1">{q.question}</p>
                                    <span className={`flex-shrink-0 text-xs font-black uppercase tracking-wider px-3.5 py-2 rounded-lg border ${
                                        !answered ? "bg-slate-100 text-slate-400 border-slate-200"
                                        : isRight  ? "bg-emerald-50 text-emerald-600 border-emerald-150"
                                        : "bg-rose-50 text-rose-600 border-rose-150"
                                    }`}>
                                        {!answered ? "Skipped" : isRight ? "Correct" : "Wrong"}
                                    </span>
                                </div>
                                <div className="ml-13 space-y-2">
                                    {q.options?.map(opt => {
                                        const optId = String(opt.id);
                                        const isCorrect = serverCorrect 
                                            ? serverCorrectIds.includes(optId)
                                            : (optId === String(correctId) || opt.isRight || opt.isCorrect);
                                            
                                        const isSelected = selectedIds.includes(optId);
                                        return (
                                            <div key={opt.id} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-bold transition-all border ${
                                                isCorrect  ? "bg-emerald-50 text-emerald-700 border-emerald-200 shadow-sm shadow-emerald-500/5"
                                                : isSelected && !isCorrect ? "bg-rose-50 text-rose-600 border-rose-200 shadow-sm shadow-rose-500/5"
                                                : "text-slate-600 border-slate-100 bg-slate-50/30"
                                            }`}>
                                                {isCorrect  ? <Check  size={14} className="text-emerald-500 flex-shrink-0" strokeWidth={3} />
                                                : isSelected ? <XCircle size={14} className="text-rose-400 flex-shrink-0" />
                                                : <div className="w-3.5 h-3.5 flex-shrink-0" />}
                                                {opt.answer}
                                                {isCorrect && <span className="ml-auto text-xs font-black text-emerald-600 bg-emerald-100/50 px-2 py-0.5 rounded-md uppercase tracking-wider">Correct answer</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ─────────────────── AssessmentProtocol (Full Page) ─────────────────── */
const AssessmentProtocol = ({ step, setStep, quiz, agreed, setAgreed, onProceed, onReady, cameraStream, initCamera, cameraError, onCancel, testMode, onSelectMode }) => {
    if (!quiz) return null;

    const steps = testMode === "sample"
        ? [{ id: 1, name: "Instructions", icon: <FileText size={16} /> }]
        : [
            { id: 1, name: "Instructions", icon: <FileText size={16} /> },
            { id: 2, name: "Identity Check", icon: <UserCheck size={16} /> }
        ];

    return (
        <div className="min-h-[60vh] flex flex-col">
            {/* Header / Progress */}
            <div className="bg-white rounded-2xl border border-slate-200 p-3.5 mb-3 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="bg-[#264b82] p-2.5 rounded-xl shadow-lg shadow-blue-900/10">
                            <Shield className="text-white w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-slate-800 tracking-tight leading-none">{quiz.name}</h2>
                        </div>
                    </div>

                    {testMode === "sample" ? (
                        <span className="bg-blue-50 text-blue-700 text-xs font-black uppercase tracking-widest px-3.5 py-1.5 rounded-xl border border-blue-100 shadow-sm">
                            Practice Mode
                        </span>
                    ) : (
                        <div className="flex items-center gap-2">
                            {steps.map((s, idx) => (
                                <React.Fragment key={s.id}>
                                    <div className="flex items-center gap-2.5">
                                        <div className={`w-7.5 h-7.5 rounded-lg flex items-center justify-center transition-all duration-500 ${
                                            step >= s.id ? "bg-[#264b82] text-white shadow-md shadow-blue-900/10" : "bg-slate-50 text-slate-300 border border-slate-100"
                                        }`}>
                                            {step > s.id ? <Check size={13} strokeWidth={3} /> : s.icon}
                                        </div>
                                        <div className="hidden sm:block">
                                            <p className={`text-[10px] font-black uppercase tracking-wider leading-none ${step >= s.id ? "text-[#264b82]" : "text-slate-300"}`}>Step 0{s.id}</p>
                                            <p className={`text-xs font-extrabold ${step >= s.id ? "text-slate-800" : "text-slate-300"}`}>{s.name}</p>
                                        </div>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div className={`w-8 h-0.5 mx-1 rounded-full transition-all duration-500 ${step > s.id ? "bg-[#264b82]" : "bg-slate-100"}`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col lg:flex-row">
                {/* Left Panel: Context/Visual */}
                <div className="lg:w-1/4 bg-slate-50 border-r border-slate-100 p-4 flex flex-col">
                    <div className="mb-2.5">
                        <div className="w-9 h-9 bg-white rounded-lg shadow-sm border border-slate-100 flex items-center justify-center mb-2.5">
                            {step === 1 ? <Info className="text-blue-500 w-4 h-4" /> : <Video className="text-[#264b82] w-4 h-4" />}
                        </div>
                        <h3 className="text-lg font-black text-slate-800 leading-tight mb-1">
                            {step === 1 ? "Instructions" : "Identity Check"}
                        </h3>
                        <p className="text-slate-500 text-[11px] font-medium leading-relaxed">
                            {step === 1
                                ? "Review guidelines before starting."
                                : "Ensure your face is clearly visible."}
                        </p>
                    </div>

                    <div className="mt-4 sm:mt-auto space-y-1.5">
                        <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between lg:block">
                            <div className="flex items-center gap-1.5 mb-0.5">
                                <Clock size={11} className="text-amber-500" />
                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Duration</span>
                            </div>
                            <p className="text-sm font-extrabold text-slate-800">{quiz.timeLimit ? `${Math.floor(quiz.timeLimit / 60)} Minutes` : "30 Mins"}</p>
                        </div>
                        <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between lg:block">
                            <div className="flex items-center gap-1.5 mb-0.5">
                                <Target size={11} className="text-blue-500" />
                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Items</span>
                            </div>
                            <p className="text-sm font-extrabold text-slate-800">{quiz.questions?.length} Questions</p>
                        </div>
                    </div>
                </div>

                {/* Right Panel: Active Area */}
                <div className="flex-1 p-3.5 lg:p-4 overflow-y-auto">
                    {step === 1 ? (
                        <div className="max-w-2xl animate-in fade-in slide-in-from-right-4 duration-500">
                            <div className="space-y-3">
                                {/* Mode Selection Grid */}
                                <div>
                                    <h4 className="text-[10px] font-black text-[#264b82] uppercase tracking-widest mb-2">Select Assessment Mode</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {/* Sample Test Option */}
                                        <button
                                            type="button"
                                            onClick={() => onSelectMode("sample")}
                                            className={`group relative flex items-center gap-2 p-2.5 rounded-xl text-left transition-all duration-200 border-2 ${
                                                testMode === "sample"
                                                    ? "border-[#264b82] bg-[#264b82]/5 shadow-md shadow-blue-900/5"
                                                    : "border-slate-100 bg-slate-50/50 hover:bg-white hover:border-slate-300"
                                            }`}
                                        >
                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                                                testMode === "sample" ? "bg-[#264b82] text-white" : "bg-slate-200 text-slate-500"
                                            }`}>
                                                <BookOpen size={13} />
                                            </div>
                                            <div>
                                                <p className={`font-black text-sm leading-none mb-0.5 ${testMode === "sample" ? "text-[#264b82]" : "text-slate-800"}`}>Sample Test</p>
                                                <p className="text-slate-400 text-[9px] font-extrabold uppercase tracking-widest mt-0.5">Practice · Unrecorded</p>
                                            </div>
                                            {testMode === "sample" && (
                                                <div className="ml-auto w-4 h-4 rounded-full bg-[#264b82] flex items-center justify-center shadow-sm">
                                                    <Check size={9} className="text-white" strokeWidth={4} />
                                                </div>
                                            )}
                                        </button>

                                        {/* Live Test Option */}
                                        <button
                                            type="button"
                                            onClick={() => onSelectMode("live")}
                                            className={`group relative flex items-center gap-2 p-2.5 rounded-xl text-left transition-all duration-200 border-2 ${
                                                testMode === "live"
                                                    ? "border-[#264b82] bg-[#264b82]/5 shadow-md shadow-blue-900/5"
                                                    : "border-slate-100 bg-slate-50/50 hover:bg-white hover:border-slate-300"
                                            }`}
                                        >
                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                                                testMode === "live" ? "bg-[#264b82] text-white" : "bg-slate-200 text-slate-500"
                                            }`}>
                                                <Zap size={13} />
                                            </div>
                                            <div>
                                                <p className={`font-black text-sm leading-none mb-0.5 ${testMode === "live" ? "text-[#264b82]" : "text-slate-800"}`}>Live Test</p>
                                                <p className="text-slate-400 text-[9px] font-extrabold uppercase tracking-widest mt-0.5">Official Attempt · Proctoring</p>
                                            </div>
                                            {testMode === "live" && (
                                                <div className="ml-auto w-4 h-4 rounded-full bg-[#264b82] flex items-center justify-center shadow-sm">
                                                    <Check size={9} className="text-white" strokeWidth={4} />
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <ClipboardList size={13} className="text-[#264b82]" />
                                        <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Assessment Rules & Guidelines</h4>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {(testMode === "live" ? [
                                            { icon: <CheckCircle size={13} className="text-emerald-500" />, title: "Stay Focused", desc: "Keep browser tab active at all times." },
                                            { icon: <XCircle size={13} className="text-rose-500" />, title: "3-Strike Rule", desc: "Test submits automatically after 3 tab switches." },
                                            { icon: <Maximize size={13} className="text-blue-500" />, title: "Fullscreen Locked", desc: "You must remain in fullscreen mode." },
                                            { icon: <Camera size={13} className="text-rose-400" />, title: "Camera Required", desc: "Keep enabled for the full exam duration." },
                                            { icon: <Monitor size={13} className="text-blue-500" />, title: "Stable Connection", desc: "Ensure reliable internet throughout." },
                                            { icon: <Shield size={13} className="text-indigo-500" />, title: "Academic Integrity", desc: "Individual work only — no collaboration." },
                                            { icon: <Eye size={13} className="text-violet-500" />, title: "Content Protected", desc: "Copy, paste, selection, and context menus disabled." },
                                            { icon: <Clock size={13} className="text-amber-500" />, title: "Manage Your Time", desc: "Monitor the timer and pace yourself." },
                                            { icon: <Flag size={13} className="text-orange-500" />, title: "Flag & Review", desc: "Mark questions to revisit before final submission." },
                                        ] : [
                                            { icon: <CheckCircle size={13} className="text-emerald-500" />, title: "Practice Mode", desc: "Scores won't affect academic records." },
                                            { icon: <XCircle size={13} className="text-rose-500" />, title: "No Penalties", desc: "No tab-switching restrictions or warnings." },
                                            { icon: <VideoOff size={13} className="text-slate-500" />, title: "Camera Optional", desc: "Take this practice exam without a webcam." },
                                            { icon: <Clock size={13} className="text-amber-500" />, title: "Timed Practice", desc: "Timer runs to help you pace yourself." },
                                            { icon: <Flag size={13} className="text-orange-500" />, title: "Flag & Review", desc: "Revisit flagged questions before finishing." },
                                            { icon: <Monitor size={13} className="text-blue-500" />, title: "Stable Connection", desc: "Ensures your responses submit correctly." },
                                        ]).map((item, i) => (
                                            <div key={i} className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-100 rounded-lg hover:bg-slate-100/50 transition-colors">
                                                <div className="flex-shrink-0 w-6.5 h-6.5 rounded-md bg-white border border-slate-150 flex items-center justify-center shadow-sm">
                                                    {item.icon}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-extrabold text-slate-800 text-[11px] leading-tight">{item.title}</p>
                                                    <p className="text-slate-500 text-[9px] font-medium leading-snug mt-0.5">{item.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {quiz.guidelines && (
                                    <div className="p-2.5 bg-blue-50/50 border border-blue-100 rounded-xl">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                            <Zap size={11} className="text-blue-600" />
                                            <span className="text-[9px] font-black text-blue-900 uppercase tracking-widest">Custom Guidelines</span>
                                        </div>
                                        <p className="text-blue-800/80 text-[11px] font-bold leading-relaxed whitespace-pre-wrap">{quiz.guidelines}</p>
                                    </div>
                                )}

                                <div className="pt-2 border-t border-slate-100">
                                    <label className="flex items-center gap-2.5 cursor-pointer group">
                                        <div className="relative">
                                            <input type="checkbox" className="peer sr-only" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                                            <div className="w-5 h-5 border-2 border-slate-200 rounded-md bg-white peer-checked:bg-[#264b82] peer-checked:border-[#264b82] transition-all flex items-center justify-center shadow-sm">
                                                {agreed && <Check size={10} className="text-white" strokeWidth={4} />}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-slate-800 font-extrabold text-sm leading-none">I accept the assessment protocol</p>
                                            <p className="text-slate-400 text-[10px] font-semibold mt-0.5">By proceeding, you agree to all rules listed above.</p>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-md mx-auto animate-in fade-in slide-in-from-right-4 duration-500">
                            <div className="bg-slate-900 rounded-2xl p-4 shadow-xl">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-1.5">
                                        <div className={`w-1.5 h-1.5 rounded-full ${cameraStream ? "bg-emerald-500 animate-pulse" : (testMode === "sample" ? "bg-slate-400" : "bg-rose-500")}`} />
                                        <span className="text-white font-black text-[10px] uppercase tracking-widest">
                                            {cameraStream ? "Ready" : (testMode === "sample" ? "Off (Optional)" : "System Off")}
                                        </span>
                                    </div>
                                    {cameraStream && <span className="text-emerald-400 text-[10px] font-black uppercase tracking-widest">Identity Validated</span>}
                                </div>

                                <div className="aspect-video rounded-xl bg-slate-800 overflow-hidden border border-white/5 relative">
                                    {cameraStream ? <CameraFeed stream={cameraStream} /> : (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                                            <div className="bg-slate-700/50 w-11 h-11 rounded-xl flex items-center justify-center mb-3">
                                                <VideoOff size={20} className="text-slate-400" />
                                            </div>
                                            <p className="text-white text-xs font-bold mb-3">
                                                {testMode === "sample" ? "Camera Optional" : "Camera Required"}
                                            </p>
                                            <button onClick={initCamera}
                                                className="bg-white text-slate-900 px-5 py-2.5 rounded-lg font-bold text-[10px] hover:scale-105 transition-all active:scale-95 shadow-lg">
                                                Enable Camera
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {cameraError && (
                                    <div className="mt-3 p-2.5 bg-rose-500/10 border border-rose-500/20 rounded-lg flex items-center gap-1.5 text-rose-400 text-[10px] font-bold">
                                        <AlertCircle size={12} /> {cameraError}
                                    </div>
                                )}
                            </div>

                            <div className="mt-3 flex gap-2">
                                <div className="flex-1 flex items-center gap-2 p-2 bg-blue-50 border border-blue-100 rounded-lg">
                                    <UserCheck size={14} className="text-blue-600" />
                                    <p className="text-blue-900 text-[10px] font-extrabold leading-tight">Centered & Well-lit.</p>
                                </div>
                                <div className="flex-1 flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-100 rounded-lg">
                                    <Shield size={14} className="text-emerald-600" />
                                    <p className="text-emerald-900 text-[10px] font-extrabold leading-tight">Secure session.</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="mt-4 flex items-center justify-between">
                <button onClick={onCancel} className="px-3.5 py-1.5 text-slate-400 font-bold hover:text-slate-600 transition-colors text-xs flex items-center gap-1.5">
                    <ArrowLeft size={14} /> Exit
                </button>

                {step > 0 && (
                    <div className="flex items-center gap-2">
                        {step === 2 && (
                            <button onClick={() => setStep(1)} className="px-5 py-2.5 bg-slate-100 text-slate-600 rounded-lg font-bold text-[11px] hover:bg-slate-200 transition-all">
                                Back
                            </button>
                        )}
                        <button
                                disabled={step === 1 ? !agreed : (testMode === "sample" ? false : !cameraStream)}
                                onClick={step === 1 ? onProceed : onReady}
                                className={`px-7 py-3 rounded-lg font-black text-xs uppercase tracking-wider flex items-center gap-1.5 transition-all ${
                                    (step === 1 ? agreed : (testMode === "sample" ? true : cameraStream))
                                        ? "bg-[#264b82] text-white shadow-lg shadow-blue-900/10 hover:scale-105 active:scale-95"
                                        : "bg-slate-100 text-slate-300 cursor-not-allowed"
                                }`}
                            >
                                {step === 1 ? (testMode === "sample" ? "Start Test" : "Verify Identity") : "Ready to Start"}
                                <ArrowRight size={14} />
                            </button>
                    </div>
                )}
            </div>
        </div>
    );
};

/* ─────────────────── ReadyConfirmModal ─────────────────── */
const ReadyConfirmModal = ({ isOpen, onClose, onConfirm, quiz }) => (
    <Modal isOpen={isOpen} onClose={onClose}>
        <div className="max-w-md w-full mx-auto bg-white rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-gradient-to-br from-[#1e3a6e] to-[#264b82] p-8 text-center relative">
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white rounded-full -mr-12 -mt-12" />
                </div>
                <div className="relative z-10">
                    <div className="w-16 h-16 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                        <PlayCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-black text-white mb-1 tracking-tight">Are you ready?</h3>
                    <p className="text-blue-100 text-sm font-medium">The timer will begin as soon as you start.</p>
                </div>
            </div>
            <div className="p-8 space-y-5">
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 space-y-3">
                    <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-[9px] font-black uppercase tracking-widest">Protocol</span>
                        <span className="text-emerald-500 font-black text-[9px] uppercase tracking-widest flex items-center gap-1.5">
                            <CheckCircle2 size={10} strokeWidth={3} /> Verified
                        </span>
                    </div>
                </div>

                <div className="flex flex-col gap-2.5">
                    <button onClick={onConfirm}
                        className="w-full py-4 bg-[#264b82] text-white rounded-xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-blue-900/30 hover:scale-[1.02] active:scale-95 transition-all">
                        Start My Exam
                    </button>
                    <button onClick={onClose}
                        className="w-full py-3 bg-white text-slate-400 rounded-xl font-bold text-xs hover:text-slate-600 transition-all">
                        Review Protocol
                    </button>
                </div>
            </div>
        </div>
    </Modal>
);

/* ─────────────────── InstructionModal (REPLACED BY PROTOCOL) ─────────────────── */
const InstructionModal = () => null;

/* ─────────────────── SubmitConfirmModal ─────────────────── */
const SubmitConfirmModal = ({ isOpen, onClose, onConfirm, total, answered, flagged }) => {
    const unanswered = total - answered;
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="max-w-md w-full mx-auto bg-white rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
                <div className="bg-gradient-to-br from-[#264b82] to-indigo-700 px-8 py-6 text-center">
                    <div className="w-14 h-14 bg-white/15 border border-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <CheckSquare className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-black text-white mb-1">Submit Quiz?</h3>
                    <p className="text-blue-100 text-sm font-semibold">This action cannot be undone.</p>
                </div>
                <div className="p-8 space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: "Answered", value: answered, color: "text-emerald-600", bg: "bg-emerald-50" },
                            { label: "Skipped",  value: unanswered, color: "text-rose-500",    bg: "bg-rose-50"   },
                            { label: "Flagged",  value: flagged,   color: "text-amber-600",  bg: "bg-amber-50"  },
                        ].map(s => (
                            <div key={s.label} className={`${s.bg} rounded-2xl p-3 text-center`}>
                                <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                                <p className="text-slate-400 text-xs font-black uppercase tracking-wider mt-1">{s.label}</p>
                            </div>
                        ))}
                    </div>
                    {(unanswered > 0 || flagged > 0) && (
                        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-100 rounded-xl p-3">
                            <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                            <p className="text-amber-700 text-xs font-extrabold leading-tight">
                                {unanswered > 0 && `${unanswered} skipped. `}
                                {flagged > 0 && `${flagged} flagged.`}
                            </p>
                        </div>
                    )}
                    <div className="flex gap-3">
                        <button onClick={onClose} className="flex-1 py-3.5 bg-white border border-slate-200 text-slate-700 rounded-2xl font-black text-xs uppercase tracking-wider hover:bg-slate-50 transition-all active:scale-95">Review</button>
                        <button onClick={onConfirm} className="flex-1 py-3.5 bg-[#264b82] text-white rounded-2xl font-black text-xs uppercase tracking-wider hover:bg-[#1e3a6e] shadow-lg shadow-blue-900/20 transition-all active:scale-95">Submit</button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

/* ─────────────────── ExitConfirmModal ─────────────────── */
const ExitConfirmModal = ({ isOpen, onClose, onConfirm }) => (
    <Modal isOpen={isOpen} onClose={onClose}>
        <div className="max-w-md w-full mx-auto bg-white rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-gradient-to-br from-rose-500 to-rose-700 px-8 py-6 text-center">
                <div className="w-14 h-14 bg-white/15 border border-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <LogOut className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-black text-white mb-1">Exit Session?</h3>
                <p className="text-rose-100 text-sm font-semibold">All progress will be lost.</p>
            </div>
            <div className="p-8 space-y-4">
                <div className="flex items-start gap-2.5 bg-rose-50 border border-rose-100 rounded-xl p-3">
                    <AlertTriangle size={14} className="text-rose-400 flex-shrink-0 mt-0.5" />
                    <p className="text-rose-700 text-xs font-extrabold leading-tight">Your answers and time will not be saved.</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 py-3.5 bg-white border border-slate-200 text-slate-700 rounded-2xl font-black text-xs uppercase tracking-wider hover:bg-slate-50 transition-all active:scale-95">Stay</button>
                    <button onClick={onConfirm} className="flex-1 py-3.5 bg-rose-500 text-white rounded-2xl font-black text-xs uppercase tracking-wider hover:bg-rose-600 shadow-lg shadow-rose-500/20 transition-all active:scale-95">Exit</button>
                </div>
            </div>
        </div>
    </Modal>
);

/* ─────────────────── TestModeModal ─────────────────── */
const TestModeModal = ({ isOpen, onClose, onSelect }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="max-w-lg w-full bg-white rounded-3xl overflow-hidden shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-300">

                {/* Header */}
                <div className="px-7 pt-6 pb-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="bg-[#264b82] p-2 rounded-xl shadow-md shadow-blue-900/15">
                            <Layers size={16} className="text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-slate-800 tracking-tight leading-none">Select Test Mode</h3>
                            <p className="text-slate-400 text-xs font-semibold mt-1">Choose how you want to take this assessment</p>
                        </div>
                    </div>
                </div>

                {/* Mode Cards */}
                <div className="p-5 grid grid-cols-2 gap-3">

                    {/* Sample Test */}
                    <button
                        onClick={() => onSelect("sample")}
                        className="group relative flex flex-col text-left p-5 bg-slate-50 border-2 border-slate-100 rounded-2xl hover:border-slate-300 hover:bg-white hover:shadow-md transition-all duration-200"
                    >
                        <div className="w-10 h-10 bg-white rounded-xl border border-slate-200 flex items-center justify-center mb-4 shadow-sm group-hover:border-slate-300 transition-all">
                            <BookOpen size={18} className="text-slate-500" />
                        </div>

                        <p className="font-black text-slate-800 text-base leading-none mb-1.5">Sample Test</p>
                        <span className="inline-flex text-[10px] font-black uppercase tracking-wider text-slate-400 bg-slate-200 px-2.5 py-0.5 rounded-full mb-4 w-fit">Practice Mode</span>

                        <div className="space-y-2 flex-1">
                            {[
                                "Score not recorded",
                                "No camera required",
                                "Unlimited attempts",
                                "Same question format",
                            ].map(f => (
                                <div key={f} className="flex items-center gap-2 text-xs font-bold text-slate-500">
                                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                                    {f}
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Select</span>
                            <div className="w-6 h-6 rounded-lg bg-slate-200 group-hover:bg-slate-300 flex items-center justify-center transition-all">
                                <ChevronRight size={13} className="text-slate-500" />
                            </div>
                        </div>
                    </button>

                    {/* Live Test */}
                    <button
                        onClick={() => onSelect("live")}
                        className="group relative flex flex-col text-left p-5 bg-[#264b82] border-2 border-[#264b82] rounded-2xl hover:bg-[#1e3a6e] hover:border-[#1e3a6e] transition-all duration-200 shadow-lg shadow-blue-900/25"
                    >
                        <div className="absolute top-3.5 right-3.5">
                            <span className="text-[8px] font-black uppercase tracking-widest bg-white/15 border border-white/20 text-white/80 px-2 py-0.5 rounded-full">
                                Official
                            </span>
                        </div>

                        <div className="w-10 h-10 bg-white/15 rounded-xl border border-white/20 flex items-center justify-center mb-4">
                            <Zap size={18} className="text-white" />
                        </div>

                        <p className="font-black text-white text-base leading-none mb-1.5">Live Test</p>
                        <span className="inline-flex text-[10px] font-black uppercase tracking-wider text-white/50 bg-white/10 border border-white/15 px-2.5 py-0.5 rounded-full mb-4 w-fit">
                            Official Attempt
                        </span>

                        <div className="space-y-2 flex-1">
                            {[
                                "Score saved to record",
                                "Camera monitoring on",
                                "Counts toward progress",
                                "Timed assessment",
                            ].map(f => (
                                <div key={f} className="flex items-center gap-2 text-xs font-bold text-white/80">
                                    <Check size={10} className="text-emerald-300 flex-shrink-0" strokeWidth={3} />
                                    {f}
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 pt-3 border-t border-white/15 flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-wider text-white/40">Select</span>
                            <div className="w-6 h-6 rounded-lg bg-white/15 group-hover:bg-white/25 flex items-center justify-center transition-all">
                                <ChevronRight size={13} className="text-white" />
                            </div>
                        </div>
                    </button>
                </div>

                {/* Footer */}
                <div className="px-7 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-400">Both modes use the same question set.</p>
                    <button onClick={onClose} className="text-slate-400 font-black text-sm hover:text-slate-600 transition-colors px-4 py-2 rounded-xl hover:bg-slate-100">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────── CameraFeed ─────────────────── */
const CameraFeed = ({ stream }) => {
    const videoRef = React.useRef(null);
    React.useEffect(() => {
        if (videoRef.current && stream) videoRef.current.srcObject = stream;
    }, [stream]);
    if (!stream) return null;
    return (
        <div className="relative w-full aspect-video overflow-hidden rounded-xl bg-slate-900">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
            {/* <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/50 backdrop-blur px-2 py-1 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                <span className="text-[10px] text-white font-black uppercase tracking-widest">Live11</span>
            </div> */}
        </div>
    );
};

/* ─────────────────── WarningModal ─────────────────── */
const WarningModal = ({ isOpen, onClose, warnings }) => {
    if (!isOpen) return null;
    const strikesLeft = 3 - warnings;
    return (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="max-w-md w-full bg-white rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300 border border-rose-100">
                <div className="bg-gradient-to-br from-rose-500 to-rose-700 p-8 text-center relative">
                    <div className="relative z-10">
                        <div className="w-16 h-16 bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                            <AlertTriangle className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-2xl font-black text-white mb-1 tracking-tight">Warning Issued</h3>
                        <p className="text-rose-100 text-sm font-medium">Tab switching is strictly prohibited.</p>
                    </div>
                </div>
                <div className="p-8 space-y-5 text-center">
                    <p className="text-slate-600 font-medium">You have navigated away from the exam tab. This is a violation of the assessment protocol.</p>
                    <div className="inline-flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-600 px-4 py-2 rounded-xl font-bold text-sm">
                        <XCircle size={18} /> {strikesLeft} {strikesLeft === 1 ? "strike" : "strikes"} remaining
                    </div>
                    <p className="text-slate-400 text-xs font-medium">If you reach 0 strikes, your exam will be automatically submitted.</p>
                    <button onClick={onClose}
                        className="w-full py-4 bg-rose-500 text-white rounded-xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-rose-500/30 hover:scale-[1.02] active:scale-95 transition-all">
                        I Understand
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuizExam;
